import React from "react";
import { Box, Copy, Network } from "lucide-react";
import { ExplosionIcon } from "./Icons";
import {
  INSPECTOR_CHROME_HOVER_SURFACE,
  INSPECTOR_CHROME_NO_FOCUS_RING,
} from "../../utils/inspectorChromeButtonClasses";
import Tooltip from "./Tooltip";

const PhysicsBlockedOverlay = ({ iconClass }) => (
  <svg
    className={`pointer-events-none absolute inset-0 ${iconClass}`}
    viewBox="0 0 16 16"
    aria-hidden
  >
    <line
      x1="0"
      y1="14"
      x2="14"
      y2="0"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const BUTTON_CLASS_NAME_DEFAULT = [
  "h-8 w-8 rounded-sm border-0 bg-transparent p-0 text-sm transition-colors flex items-center justify-center text-gray-200",
  INSPECTOR_CHROME_NO_FOCUS_RING,
  INSPECTOR_CHROME_HOVER_SURFACE,
  "disabled:opacity-40 disabled:pointer-events-none disabled:!pointer-events-auto",
].join(" ");

const BUTTON_CLASS_NAME_COMPACT = [
  "h-7 w-7 rounded-sm border-0 bg-transparent p-0 text-sm transition-colors flex items-center justify-center text-gray-200",
  INSPECTOR_CHROME_NO_FOCUS_RING,
  INSPECTOR_CHROME_HOVER_SURFACE,
  "disabled:opacity-40 disabled:pointer-events-none disabled:!pointer-events-auto",
].join(" ");

export function buildDuplicateCollisionAction({ geomKey, onDuplicateCollision, onHover }) {
  if (!geomKey || !onDuplicateCollision) return null;
  return {
    key: "duplicate",
    label: "Duplicate collision",
    icon: <Copy className="w-4 h-4" aria-hidden="true" />,
    onHover,
    onClick: () => onDuplicateCollision(geomKey),
  };
}

const DuplicateMenuContent = ({
  onDuplicateBody = null,
  onDuplicateBranch = null,
  onDuplicateCollision = null,
  onDuplicateItem = null,
  duplicateItemLabel = "Duplicate",
  disabled = false,
  /** When true with `disabled`, duplicate tooltips explain physics and icons show a blocked overlay. */
  physicsRunning = false,
  size = "default",
  buttonExtraProps = null,
  /** When true, body/collision use the same Copy icon as context-menu duplicate actions (see buildDuplicateCollisionAction). */
  useCopyIconForBodyAndCollision = false,
}) => {
  const buttonClassName =
    size === "compact" ? BUTTON_CLASS_NAME_COMPACT : BUTTON_CLASS_NAME_DEFAULT;
  const iconClass = size === "compact" ? "w-3.5 h-3.5" : "w-4 h-4";
  const extra = buttonExtraProps && typeof buttonExtraProps === "object" ? buttonExtraProps : {};
  const BodyIcon = useCopyIconForBodyAndCollision ? Copy : Box;
  const CollisionIcon = useCopyIconForBodyAndCollision ? Copy : ExplosionIcon;

  const tooltipFor = (actionTitle) =>
    disabled && physicsRunning ? "Stop physics to duplicate" : actionTitle;

  return (
    <div className="flex items-center gap-1">
      {onDuplicateBody ? (
        <Tooltip content={tooltipFor("Duplicate body only")}>
          <button
            type="button"
            aria-label={tooltipFor("Duplicate body only")}
            disabled={disabled}
            onClick={onDuplicateBody}
            className={buttonClassName}
            {...extra}
          >
            <span className={`relative inline-flex ${iconClass}`}>
              <BodyIcon className={iconClass} aria-hidden="true" />
              {physicsRunning ? <PhysicsBlockedOverlay iconClass={iconClass} /> : null}
            </span>
          </button>
        </Tooltip>
      ) : null}
      {onDuplicateBranch ? (
        <Tooltip content={tooltipFor("Duplicate branch")}>
          <button
            type="button"
            aria-label={tooltipFor("Duplicate branch")}
            disabled={disabled}
            onClick={onDuplicateBranch}
            className={buttonClassName}
            {...extra}
          >
            <span className={`relative inline-flex ${iconClass}`}>
              <Network className={iconClass} aria-hidden="true" />
              {physicsRunning ? <PhysicsBlockedOverlay iconClass={iconClass} /> : null}
            </span>
          </button>
        </Tooltip>
      ) : null}
      {onDuplicateCollision ? (
        <Tooltip content={tooltipFor("Duplicate collision")}>
          <button
            type="button"
            aria-label={tooltipFor("Duplicate collision")}
            disabled={disabled}
            onClick={onDuplicateCollision}
            className={buttonClassName}
            {...extra}
          >
            <span className={`relative inline-flex ${iconClass}`}>
              <CollisionIcon className={iconClass} aria-hidden="true" />
              {physicsRunning ? <PhysicsBlockedOverlay iconClass={iconClass} /> : null}
            </span>
          </button>
        </Tooltip>
      ) : null}
      {onDuplicateItem ? (
        <Tooltip content={tooltipFor(duplicateItemLabel)}>
          <button
            type="button"
            aria-label={tooltipFor(duplicateItemLabel)}
            disabled={disabled}
            onClick={onDuplicateItem}
            className={buttonClassName}
            {...extra}
          >
            <span className={`relative inline-flex ${iconClass}`}>
              <Copy className={iconClass} aria-hidden="true" />
              {physicsRunning ? <PhysicsBlockedOverlay iconClass={iconClass} /> : null}
            </span>
          </button>
        </Tooltip>
      ) : null}
    </div>
  );
};

export default React.memo(DuplicateMenuContent);
